import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { channelWa } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert, Stepper } from '@repletoryreach/sdk-ui/data-display';
import { Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../i18n/I18nProvider';
import { META_PAYMENTS_URL } from '../../lib/meta-billing';
import { MetaRefusalNotice, type Refusal } from './MetaRefusalNotice';

/** Where a plan that includes an own WhatsApp account is chosen: the plan chooser, opened on Growth. */
export const OWN_ACCOUNT_UPGRADE_PATH = '/billing?plan=growth';

/*
 * The guided WhatsApp connection.
 *
 * Someone presses one button, signs in with Facebook, picks the number they
 * want to use, and it is connected. What used to sit here instead was a notice
 * saying self-service connection would arrive one day and, underneath it, a
 * form asking for a "Meta phone-number-id": a value most people have never
 * heard of, on the one screen where a new account is definitionally stuck.
 *
 * Nothing secret passes through this file. The window returns a short-lived
 * code; the platform exchanges it. The app id, the configuration id and the
 * version all come from the platform too, so none of them is baked into this
 * bundle and a change at Meta does not need a release here.
 */

/** Facebook's JS SDK, as much of it as this file touches. */
interface FacebookSdk {
  init(options: { appId: string; autoLogAppEvents?: boolean; xfbml?: boolean; version: string }): void;
  login(
    callback: (response: { authResponse?: { code?: string } | null; status?: string }) => void,
    options: Record<string, unknown>
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_ELEMENT_ID = 'rr-facebook-jssdk';

/** What Meta's window posts back while someone works through it. */
interface EmbeddedSignupMessage {
  type?: string;
  /** `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` ends a coexistence sign-up (the number stays on the app). */
  event?: 'FINISH' | 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' | 'CANCEL' | 'ERROR' | string;
  data?: {
    phone_number_id?: string;
    waba_id?: string;
    current_step?: string;
    error_message?: string;
  };
}

/** The phases this component shows, before the platform's own steps take over. */
type Phase = 'idle' | 'opening' | 'waiting' | 'finishing' | 'done' | 'failed' | 'cancelled';

export function ConnectWhatsApp({
  onConnected,
  onUnavailable,
  notices = true,
  size = 'sm'
}: {
  /** Called once a number is attached, so the page can refresh its list. */
  onConnected?: () => void;
  /**
   * The sentence to show when the guided connection cannot run (null when it
   * can). A page that renders this itself, once, in a banner of its own,
   * passes `notices={false}` so the button row stays a button row: an alert
   * inside the action strip pushed the other buttons out of line.
   */
  onUnavailable?: (message: string | null) => void;
  /** Render the idle "not available" notice next to the button. */
  notices?: boolean;
  size?: 'xs' | 'sm' | 'md';
}) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>('idle');
  const [session, setSession] = useState<channelWa.SignupSession | null>(null);
  const [error, setError] = useState<{ code?: string; message?: string } | null>(null);
  // The platform's refusal in the shared shape (what is wrong, what to do,
  // which control to offer), when the failure has one. The one that matters
  // most, a number signed in to the WhatsApp Business app on a phone, carries
  // the button into the guided move.
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [config, setConfig] = useState<channelWa.EmbeddedSignupStart | null>(null);
  // Whether this can be started at all, and whose problem it is when it
  // cannot. Asked on mount and again on the way in, because the platform side
  // can be fixed while somebody has the page open.
  const [ready, setReady] = useState<Readiness | null>(null);
  /*
   * The business's own WhatsApp account (5 October 2026): Meta bills the
   * business directly, so nobody starts it without saying they agree. The tick
   * is required; pressing the button without it says so under the tick rather
   * than opening Meta's window.
   */
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState<string | null>(null);
  // Who Meta bills for the connection that finished, so the page can say what is left to do.
  const [connectedPayer, setConnectedPayer] = useState<channelWa.MetaBillingPayer | null>(null);
  const customerPays = ready?.payer === 'customer';

  // Meta's FINISH message and FB.login()'s callback arrive independently and in
  // no fixed order, so both halves are parked here and the completion fires
  // when the second one lands.
  const pending = useRef<{ code?: string; phoneNumberId?: string; wabaId?: string; sessionId?: string; coexistence?: boolean; payer?: channelWa.MetaBillingPayer | null }>({});
  const listening = useRef(false);
  const finished = useRef(false);

  /*
   * Whether this can run at all, asked once on mount.
   *
   * This used to call `start()`, which has two problems. It opens a connection
   * attempt in the database on every visit to the page, so the operator's list
   * of attempts filled up with sessions nobody had started. And it answers only
   * "is the app configured", which is the question that was green all evening
   * while nothing worked: the configuration id was there, and the system user
   * had no rights on the account.
   *
   * Preflight reads instead of writes, and asks Meta rather than only reading
   * our own row.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const answer = await preflight();
      if (!cancelled) setReady(answer);
    })();
    return () => { cancelled = true; };
  }, []);

  const finish = useCallback(async () => {
    const { code, phoneNumberId, wabaId, sessionId, coexistence } = pending.current;
    // A coexistence finish may name only the account; the platform finds the number from it.
    if (!code || (!phoneNumberId && !coexistence) || !wabaId || finished.current) return;
    finished.current = true;
    setPhase('finishing');

    try {
      const res = await channelWa.completeEmbeddedSignup({
        code,
        phone_number_id: phoneNumberId,
        waba_id: wabaId,
        session_id: sessionId
      });
      setSession(res.data);
      setConnectedPayer(pending.current.payer ?? null);
      setPhase('done');
      notifySuccess(
        t(coexistence ? 'numbers.coexist.done' : 'numbers.connect.done', { number: res.data.msisdn || res.data.display_name || phoneNumberId || '' }),
        t('numbers.connect.doneTitle')
      );
      onConnected?.();
    } catch (e) {
      // The service answers 502 with { code, message, step }. `ApiError`
      // carries the code; the message is Meta's own sentence, shown under our
      // plain-language explanation rather than instead of it.
      const api = e as { code?: string; body?: { error?: Partial<Refusal> & { plan_name?: string | null } }; message?: string };
      const body = api.body?.error;
      // The plan no longer includes the business's own account (it changed while Meta's window was open).
      if (channelWa.isOwnAccountNotOnPlan(e)) {
        setReady((r) => ({ ...(r ?? { ready: false }), ready: false, reason: 'NOT_ON_PLAN', planName: body?.plan_name ?? null, payer: 'customer' }));
        setError({ code: 'NOT_ON_PLAN' });
        setRefusal(null);
        setPhase('failed');
        return;
      }
      setError({
        code: body?.code ?? api.code,
        message: body?.message ?? api.message
      });
      setRefusal(body && typeof body.code === 'string' && typeof body.message === 'string' && (body.next_step || body.action)
        ? (body as Refusal)
        : null);
      setPhase('failed');
    }
  }, [onConnected, t]);

  /** Meta's window talks to the page through postMessage, not through FB.login(). */
  useEffect(() => {
    if (listening.current) return;
    listening.current = true;

    function onMessage(event: MessageEvent) {
      // Anything not from Facebook is somebody else's message.
      let origin: string;
      try { origin = new URL(event.origin).hostname; } catch { return; }
      if (origin !== 'facebook.com' && !origin.endsWith('.facebook.com')) return;

      let payload: EmbeddedSignupMessage;
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : (event.data as EmbeddedSignupMessage);
      } catch {
        return;   // Facebook posts other, unrelated strings on this channel
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (payload.event === 'FINISH' || payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        pending.current.phoneNumberId = payload.data?.phone_number_id;
        pending.current.wabaId = payload.data?.waba_id;
        void finish();
        return;
      }

      if (payload.event === 'CANCEL') {
        setPhase('cancelled');
        void channelWa.cancelEmbeddedSignup({ session_id: pending.current.sessionId }).catch(() => {});
        return;
      }

      if (payload.event === 'ERROR') {
        setError({ code: 'META_WINDOW_ERROR', message: payload.data?.error_message });
        setPhase('failed');
        void channelWa
          .cancelEmbeddedSignup({
            session_id: pending.current.sessionId,
            error_code: 'META_WINDOW_ERROR',
            error_message: payload.data?.error_message
          })
          .catch(() => {});
      }
    }

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      listening.current = false;
    };
  }, [finish]);

  async function connect(mode?: 'coexistence') {
    // Agreeing comes first when the business pays Meta: said under the tick, and nothing opens.
    if (customerPays && !agreed) {
      setAgreeError(t('numbers.own.agree.required'));
      return;
    }
    setError(null);
    setRefusal(null);
    setSession(null);
    setConnectedPayer(null);
    finished.current = false;
    pending.current = { coexistence: mode === 'coexistence' };
    setPhase('opening');

    /*
     * Preflight, before a single byte of Facebook's SDK is fetched.
     *
     * On the evening this was written the SDK loaded happily against an app
     * with no Embedded Signup configuration id, quietly fell back to a plain
     * Facebook login, and Meta answered "This app needs at least one supported
     * permission". Nobody in front of that screen could have done anything with
     * it. Refusing to open the window and saying why is worth more than opening
     * one that cannot finish.
     */
    const answer = await preflight();
    setReady(answer);
    if (!answer.ready) {
      // The plan refusal is shown in place of the button, with the way to a plan that includes it.
      if (!notices || answer.reason === 'NOT_ON_PLAN') { setPhase('idle'); return; }   // the page's banner already says why; the button goes back to rest
      setError({ code: answer.reason ?? 'NOT_SWITCHED_ON', message: answer.message });
      setPhase('failed');
      return;
    }
    // Who pays changed between the visit and the press (the platform's credit line came or went): agree again.
    if (answer.payer === 'customer' && !agreed) {
      setAgreeError(t('numbers.own.agree.required'));
      setPhase('idle');
      return;
    }

    let start: channelWa.EmbeddedSignupStart;
    try {
      const res = await channelWa.startEmbeddedSignup({
        ...(mode ? { mode } : {}),
        ...(answer.payer === 'customer' ? { accept_meta_billing: true } : {})
      });
      start = res.data;
      setConfig(start);
    } catch {
      setError({ code: 'START_FAILED' });
      setPhase('failed');
      return;
    }

    if (!start.available || !start.app_id || !start.config_id) {
      if (start.reason === 'NOT_ON_PLAN') {
        setReady((r) => ({ ...(r ?? { ready: false }), ready: false, reason: 'NOT_ON_PLAN', payer: 'customer' }));
        setPhase('idle');
        return;
      }
      setError({ code: start.reason ?? 'NOT_CONFIGURED' });
      setPhase('failed');
      return;
    }
    pending.current.sessionId = start.session_id ?? undefined;
    pending.current.payer = start.payer ?? answer.payer ?? null;

    let fb: FacebookSdk;
    try {
      fb = await loadFacebookSdk(start.app_id, start.graph_version);
    } catch {
      setError({ code: 'SDK_FAILED' });
      setPhase('failed');
      return;
    }

    setPhase('waiting');
    fb.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (!code) {
          // No code and no FINISH message: the window was dismissed.
          if (!finished.current) {
            setPhase((p) => (p === 'finishing' || p === 'done' ? p : 'cancelled'));
            void channelWa.cancelEmbeddedSignup({ session_id: pending.current.sessionId }).catch(() => {});
          }
          return;
        }
        pending.current.code = code;
        void finish();
      },
      {
        config_id: start.config_id,
        response_type: 'code',
        override_default_response_type: true,
        // Coexistence (3 October 2026): Meta's window for a number that stays on the WhatsApp Business app.
        extras: { setup: {}, featureType: start.feature_type ?? '', sessionInfoVersion: '3' }
      }
    );
  }

  const busy = phase === 'opening' || phase === 'waiting' || phase === 'finishing';
  const unavailable = (ready !== null && !ready.ready) || (config !== null && !config.available);
  // The plan does not include the business's own account: the sentence, and the way to a plan that does, instead of the button.
  const notOnPlan = ready?.reason === 'NOT_ON_PLAN';
  const notOnPlanMessage = notOnPlan
    ? (ready?.planName ? t('numbers.own.notOnPlan.named', { plan: ready.planName }) : t('numbers.own.notOnPlan'))
    : null;
  const unavailableMessage = unavailable
    ? (ready?.reason === 'PLAN_LIMIT_REACHED' && ready.message)
      || notOnPlanMessage
      || (ready?.reason === 'NOT_SWITCHED_ON'
        ? t('numbers.connect.error.NOT_SWITCHED_ON')
        : t('numbers.connect.unavailable'))
    : null;
  useEffect(() => { onUnavailable?.(unavailableMessage); }, [onUnavailable, unavailableMessage]);
  // A plan that is full is the customer's own ceiling, and the sentence to show
  // is the one the service wrote. Everything else is ours to explain.

  // Rendered as siblings inside the caller's wrapping Group: the buttons sit
  // on the row, and each status block takes a full line beneath it, so a
  // notice never pushes the neighbouring buttons out of line. The button is
  // never disabled: pressing it re-checks and, when the page owns the notice,
  // simply leaves the banner standing.
  const full = { flexBasis: '100%' as const, minWidth: 0 };
  if (notOnPlan) {
    return (
      <div style={full}>
        <Alert color="info" data-testid="own-account-not-on-plan">
          <Stack gap={6}>
            <Text role="bodySmall">{notOnPlanMessage}</Text>
            <Group gap="sm">
              <Link to={OWN_ACCOUNT_UPGRADE_PATH}>{t('numbers.own.upgrade')}</Link>
            </Group>
          </Stack>
        </Alert>
      </div>
    );
  }
  return (
    <>
      {/* Meta bills the business directly: agreed to before anything opens. The hint, with the place to add a card, sits under the tick. */}
      {customerPays && phase !== 'done' && (
        <div style={full}>
          <Checkbox
            data-testid="own-account-agree"
            required
            checked={agreed}
            onChange={(checked) => { setAgreed(checked); setAgreeError(null); }}
            label={t('numbers.own.agree.label')}
            error={agreeError ?? undefined}
            description={(
              <>
                {t('numbers.own.agree.hint')}{' '}
                <a href={META_PAYMENTS_URL} target="_blank" rel="noreferrer noopener">{t('numbers.own.paymentSettings')}</a>
              </>
            )}
          />
        </div>
      )}
      <Button size={size} loading={busy && !pending.current.coexistence} disabled={busy && pending.current.coexistence} onClick={() => void connect()}>
        {t(customerPays ? 'numbers.own.connect' : 'numbers.connect.action')}
      </Button>
      {/* Coexistence (3 October 2026): keep the number on the WhatsApp Business app too. Offered only once the platform has switched it on. */}
      {ready?.coexistence && (
        <Tooltip position="bottom" maxWidth={340} label={`${t('numbers.coexist.body')} ${t('numbers.coexist.tradeoffs')}`}>
          <Button size={size} variant="default" loading={busy && pending.current.coexistence} disabled={busy && !pending.current.coexistence}
            onClick={() => void connect('coexistence')} data-testid="connect-coexistence">
            {t('numbers.coexist.action')}
          </Button>
        </Tooltip>
      )}
      {phase === 'failed' || phase === 'cancelled' ? (
        <Button size={size} variant="default" onClick={() => void connect(pending.current.coexistence ? 'coexistence' : undefined)}>{t('numbers.connect.retry')}</Button>
      ) : null}
      {ready?.coexistence && phase === 'idle' && notices && (
        <div style={full}><Alert color="info" title={t('numbers.coexist.title')} data-testid="coexistence-explained">
          <Stack gap={4}>
            <Text role="bodySmall">{t('numbers.coexist.body')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.coexist.tradeoffs')}</Text>
          </Stack>
        </Alert></div>
      )}

      {notices && unavailable && phase === 'idle' && unavailableMessage && (
        <div style={full}><Alert color="info">{unavailableMessage}</Alert></div>
      )}

      {phase === 'opening' && <div style={full}><Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.connect.opening')}</Text></div>}
      {phase === 'waiting' && <div style={full}><Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.connect.waiting')}</Text></div>}

      {(phase === 'finishing' || phase === 'done') && <div style={full}><Progress session={session} phase={phase} payer={connectedPayer ?? pending.current.payer ?? null} /></div>}

      {phase === 'cancelled' && <div style={full}><Alert color="warning">{t('numbers.connect.cancelled')}</Alert></div>}

      {phase === 'failed' && refusal && (
        <div style={full}><MetaRefusalNotice refusal={refusal} onConnected={() => { setPhase('done'); onConnected?.(); }} /></div>
      )}

      {phase === 'failed' && !refusal && (
        <div style={full}><Alert color="error" role="alert">
          <Stack gap={4}>
            {error?.code === 'PLAN_LIMIT_REACHED' && error.message ? (
              // The plan refusal already is the sentence to show, in the same
              // words the attach form uses. Wrapping it in "the message from
              // WhatsApp was" would be untrue as well as unhelpful.
              <Text role="bodySmall">{error.message}</Text>
            ) : (
              <>
                <Text role="bodySmall">{t(`numbers.connect.error.${error?.code ?? 'generic'}`, t('numbers.connect.error.generic'))}</Text>
                {/* Meta's own sentence, kept underneath rather than instead of
                    ours: support needs the original wording. */}
                {error?.message && (
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('numbers.connect.error.details', { message: error.message })}
                  </Text>
                )}
              </>
            )}
          </Stack>
        </Alert></div>
      )}

      {phase === 'done' && session?.already_connected && (
        <div style={full}><Alert color="info">{t('numbers.connect.alreadyConnected')}</Alert></div>
      )}

      {/* What is left to do on the business's own account: Meta needs a way to bill it before templates go out. */}
      {phase === 'done' && connectedPayer === 'customer' && (
        <div style={full}><PaymentMethodChecklist /></div>
      )}
    </>
  );
}

/**
 * The platform's own steps, in the order it runs them. The list comes off the
 * session rather than being written out here, so a change to the flow does not
 * silently leave this list wrong.
 */
function Progress({ session, phase, payer }: { session: channelWa.SignupSession | null; phase: Phase; payer: channelWa.MetaBillingPayer | null }) {
  const t = useT();
  const steps = session?.steps ?? ([
    'launched', 'meta_returned', 'token_exchanged', 'billing_attached', 'app_subscribed',
    'number_registered', 'credentials_stored', 'number_attached'
  ] as channelWa.SignupStep[]);

  const reached = session ? steps.indexOf(session.current_step) : 1;
  const active = phase === 'done' ? steps.length : Math.max(reached, 1);

  return (
    <div className="rr-numbers__connect-steps">
      <Stepper active={active} orientation="vertical" size="xs">
        {/* On the business's own account the billing step is Meta billing the business, not our account. */}
        {steps.map((s) => (
          <Stepper.Step key={s} label={t(s === 'billing_attached' && payer === 'customer' ? 'numbers.connect.step.billing_attached.customer' : `numbers.connect.step.${s}`)} />
        ))}
      </Stepper>
    </div>
  );
}

/**
 * The one thing left to do on the business's own WhatsApp account: a payment
 * method in Meta. Shown after connecting and on the Numbers page for every
 * number Meta bills the business for.
 */
export function PaymentMethodChecklist({ count, onClose }: { count?: number; onClose?: () => void }) {
  const t = useT();
  return (
    <Alert color="warning" title={t('numbers.own.checklist.title')} data-testid="own-account-checklist"
      withCloseButton={!!onClose} onClose={onClose}>
      <Stack gap={6}>
        <Text role="bodySmall">
          {count && count > 1 ? t('numbers.own.checklist.bodyMany', { count }) : t('numbers.own.checklist.body')}
        </Text>
        <a href={META_PAYMENTS_URL} target="_blank" rel="noreferrer noopener">{t('numbers.sendBlock.fix')}</a>
      </Stack>
    </Alert>
  );
}

/** What preflight told us, plus the sentence to show when the answer carries one. */
export interface Readiness {
  ready: boolean;
  reason?: string;
  message?: string;
  /** "Keep using the WhatsApp Business app" may be offered. */
  coexistence?: boolean;
  /** Who Meta would bill (5 October 2026): `customer` asks for the agreement first. */
  payer?: channelWa.MetaBillingPayer | null;
  /** The plan's name, for `NOT_ON_PLAN`. */
  planName?: string | null;
}

/**
 * Ask whether a connection can start right now.
 *
 * Never throws. Two answers matter and both come back as a refusal with a
 * reason: the plan's own ceiling, which is the customer's and carries the
 * service's own sentence, and everything on the platform side, which is not
 * theirs and gets one honest line instead of a Meta setting they cannot reach.
 * A network failure reads as the second, because from this page it is
 * indistinguishable from one.
 */
async function preflight(): Promise<Readiness> {
  try {
    const res = await channelWa.embeddedSignupPreflight();
    return {
      ready: res.data.ready,
      reason: res.data.reason,
      coexistence: res.data.coexistence_available === true,
      payer: res.data.payer ?? null,
      planName: res.data.plan_name ?? null
    };
  } catch (e) {
    if (channelWa.isPlanLimitReached(e)) {
      return { ready: false, reason: 'PLAN_LIMIT_REACHED', message: e.body.error.message };
    }
    return { ready: false, reason: 'NOT_SWITCHED_ON' };
  }
}

/**
 * Load Facebook's JS SDK once and initialise it against the app the platform
 * named. Resolves with the SDK; rejects when the script cannot be fetched,
 * which is what happens behind a content blocker or an offline machine.
 */
function loadFacebookSdk(appId: string, version: string): Promise<FacebookSdk> {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve(window.FB);
      return;
    }

    const existing = document.getElementById(SDK_ELEMENT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    window.fbAsyncInit = () => {
      if (!window.FB) { reject(new Error('sdk-missing')); return; }
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve(window.FB);
    };

    script.onerror = () => reject(new Error('sdk-blocked'));

    if (!existing) {
      script.id = SDK_ELEMENT_ID;
      script.src = SDK_SRC;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }

    // A blocked script sometimes neither loads nor errors. Fail on a timer so
    // the button stops spinning and says something.
    window.setTimeout(() => reject(new Error('sdk-timeout')), 15_000);
  });
}
